'use client'

import { Fragment, useEffect, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import { isEmpty } from 'lodash'

import { IActionRepeaterValue, TypographyGroupValue } from '@makyoapp/makyo-common'
import {
  BaseComponent,
  Button,
  ComponentAlignment,
  CornerRadius,
  FormContainer,
  getTextColorStyles,
  getTypographyTokenStyle,
  MaterialIcon,
  RawSpacingInput,
  RenderText,
  Skeleton,
  SkeletonGroup,
} from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos } from '@/core/models/app-components/types'
import { cn } from '@/lib/cn'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { ComponentSettingKey } from '@/shared/builder'
import { DEFAULT_DEBOUNCE_MS } from '@/shared/constants'
import { getSafely } from '@/shared/safely'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { PluginFormControl } from './plugin-form'

export interface CompLoginFormWrapperProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  language: string
}

/**
 * CompLoginFormWrapperCore
 */
export const CompLoginFormWrapperCore = ({
  infos: { component, settings, currentParentBg },
  action,
  language,
}: CompLoginFormWrapperProps) => {
  // access app auth context
  const {
    isUserLoggedIn,
    currentUser,
    handleLogout,
    handleAuthLogin,
    isLoading: isAuthLoading,
    authForm,
    authPlugin,
  } = useAppAuthContext()
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  // read search params from url (?next=:slug)
  const searchParams = new URLSearchParams(window.location.search)
  const postLoginRedirectSlug = searchParams.get('next') as string | undefined

  // ========================= design =========================
  // base
  const width = getSafely(settings, [ComponentSettingKey.LoginFormDesignWidth], ['100%'])[0]
  const alignment = getSafely(settings, [ComponentSettingKey.LoginFormDesignHAlign], 'parent') as ComponentAlignment
  const margin = settings[ComponentSettingKey.LoginFormDesignMargin] as RawSpacingInput

  // login-form
  const cornerRadius = settings[ComponentSettingKey.LoginFormDesignFieldShape] || ('small' as CornerRadius)

  const buttonPadding = settings[ComponentSettingKey.LoginFormDesignButtonPadding] as RawSpacingInput
  const buttonColorVariant = settings[ComponentSettingKey.LoginFormDesignButtonColorVariant] ?? 'primary-40'
  const buttonCornerRadius = settings[ComponentSettingKey.LoginFormDesignButtonShape] || ('medium' as CornerRadius)
  const buttonSize = settings[ComponentSettingKey.LoginFormDesignButtonSize] || 'lg'

  const typoSetting = settings[ComponentSettingKey.LoginFormDesignTypography] as TypographyGroupValue
  const titleTypo = typoSetting[ComponentSettingKey.LoginFormDesignTitleTypography]
  const titleFontStyles = getTypographyTokenStyle(titleTypo)

  // color
  const titleColor = settings[ComponentSettingKey.LoginFormDesignTitleColor]
  const titleColorSolid = settings[ComponentSettingKey.LoginFormDesignTitleColorSolid] || 'primary-40'
  const titleColorStartGradient = settings[ComponentSettingKey.LoginFormDesignTitleColorStartGradient] || 'primary-40'
  const titleColorEndGradient = settings[ComponentSettingKey.LoginFormDesignTitleColorEndGradient] || 'primary-40'
  const titleColorGradientDirection = settings[ComponentSettingKey.LoginFormDesignTitleColorGradientDirection]

  const labelColor = settings[ComponentSettingKey.LoginFormDesignLabelColor]
  const labelColorSolid = settings[ComponentSettingKey.LoginFormDesignLabelColorSolid] || 'primary-40'
  const labelColorStartGradient = settings[ComponentSettingKey.LoginFormDesignLabelColorStartGradient] || 'primary-40'
  const labelColorEndGradient = settings[ComponentSettingKey.LoginFormDesignLabelColorEndGradient] || 'primary-40'
  const labelColorGradientDirection = settings[ComponentSettingKey.LoginFormDesignLabelColorGradientDirection]

  // ========================= action =========================
  const onLoginActions = (settings[ComponentSettingKey.LoginFormActionOnLogin] as IActionRepeaterValue[]) ?? []
  const onLogoutActions = (settings[ComponentSettingKey.LoginFormActionOnLogout] as IActionRepeaterValue[]) ?? []

  // ========================= behavior =========================
  const defaultRedirectPageId = settings[ComponentSettingKey.LoginFormBehaviorRedirect] as string | undefined

  // ========================= form state =========================

  // local State
  const [formState, setFormState] = useState<Record<string, string>>({})
  const [prevStepState, setPrevStepState] = useState<Record<string, string>>({})
  const [formErrors, setFormErrors] = useState<Record<string, string | undefined>>({})
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState(1)
  const validationTimeoutRef = useRef<NodeJS.Timeout>()

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  // login handler
  const handleAppLogin = async () => {
    if (loading) return

    const data = formState

    setLoading(true)

    await handleAuthLogin({
      payload: { step, data },
      onNextStep: () => {
        // go to next step
        setPrevStepState(formState)
        setFormState({})
        setStep(step + 1)
      },
      onFinish: async () => {
        // reset state
        setFormState({})
        setStep(1)

        // execute on login action
        if (!!onLoginActions?.length) {
          await action.simulateAction({
            actions: onLoginActions,
          })
        }
      },
      onError: (err) => {
        if (step === 1) {
          // hardcode email login error message (step 1)
          setErrorMessage('Access denied. You are not authorized to log in.')
        } else {
          let errorMessage = typeof err === 'string' ? err : err.message
          setErrorMessage(errorMessage)
        }
      },
      onFinally: () => {
        setLoading(false)
      },
    })
  }

  // logout handler
  const handleAppLogout = async () => {
    setLoading(true)

    await handleLogout()

    // execute on logout action
    if (!!onLogoutActions?.length) {
      await action.simulateAction({
        actions: onLogoutActions,
      })
    }

    setLoading(false)
  }

  const validateField = (key: string, value: string) => {
    switch (key) {
      case 'email': {
        // check if its empty
        if (isEmpty(value)) {
          return 'Email is required'
        }
        // check if its a valid email
        const regexPattern = new RegExp('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')
        const isValid = regexPattern.test(value)
        return isValid ? undefined : 'Invalid email address'
      }
      default:
        return undefined
    }
  }

  const handleChange = (key: string, value: string) => {
    setFormState((prev) => ({ ...prev, [key]: value }))
    setErrorMessage(undefined)

    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
    }

    validationTimeoutRef.current = setTimeout(() => {
      const error = validateField(key, value)
      setFormErrors((prev) => ({ ...prev, [key]: error }))
    }, DEFAULT_DEBOUNCE_MS)
  }

  const handleGoBack = () => {
    setFormState(prevStepState)
    setStep(step - 1)
  }

  // ========================= render =========================
  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
    }
  }, [])

  if (isAuthLoading) {
    return (
      <SkeletonGroup backgroundColor={currentParentBg!}>
        <div className='w-full flex flex-col gap-4 p-4'>
          <Skeleton className='w-full h-10' />
          <Skeleton className='w-full h-10' />
          <Skeleton className='w-full h-10' />
        </div>
      </SkeletonGroup>
    )
  }

  if (!authForm || !authPlugin) {
    return <Trans>AUTH_PLUGIN_NOT_FOUND</Trans>
  }

  const currentStep = authForm._Steps[step - 1]
  const showGoBackButton = step > 1
  const stepLabel = currentStep._Label[language] ?? currentStep._Label['en']
  const stepActionLabel = currentStep._ActionLabel[language] ?? currentStep._ActionLabel['en']

  const noErrors = Object.values(formErrors).every((error) => !error)
  const noEmptyValues = Object.keys(formState).length > 0 && Object.values(formState).every((value) => !!value.trim())
  const isValidInput = noErrors && noEmptyValues

  const loginFormComponent = (
    <BaseComponent
      id={component._id}
      width={width}
      alignment={alignment}
      margin={margin}
      backgroundColor={currentParentBg}
      style={{ zIndex: 1, position: 'relative' }}
    >
      {isUserLoggedIn && currentUser ? (
        <div className='flex flex-col gap-4'>
          <div>
            <Trans>User logged in as: {currentUser.email}</Trans>
          </div>
          <Button
            id={`${component._id}-logout-button`}
            colorVariant={buttonColorVariant}
            cornerRadius={buttonCornerRadius}
            disabled={loading}
            fullWidth
            onClick={handleAppLogout}
            icon={<MaterialIcon icon='logout' size={18} />}
          >
            {loading ? (
              <>
                <span className='loading spin'></span>
              </>
            ) : (
              <Trans>Logout</Trans>
            )}
          </Button>
        </div>
      ) : (
        <div id={`login-form-wrapper`} className='flex flex-col gap-6'>
          <div className='min-h-5'>
            {showGoBackButton && (
              <div
                role='button'
                onClick={handleGoBack}
                className='cursor-pointer w-fit inline-flex items-center gap-1 hover:underline text-sm'
              >
                <MaterialIcon icon='arrow_back' size={14} />
                <Trans>Go Back</Trans>
              </div>
            )}
          </div>

          <div className='flex flex-col gap-4'>
            <div
              className={cn('flex justify-center text-center', `text-on-${currentParentBg}`)}
              style={titleFontStyles}
            >
              <RenderText
                colorType={titleColor}
                colorSolid={titleColorSolid}
                colorStartGradient={titleColorStartGradient}
                colorEndGradient={titleColorEndGradient}
                colorGradientDirection={titleColorGradientDirection}
                value={stepLabel}
                backgroundColor={currentParentBg || 'neutral-100'}
                typography={titleTypo}
                limit={10}
                alignment='parent'
              />
            </div>

            <FormContainer
              width='100%'
              backgroundColor={currentParentBg}
              cornerRadius={cornerRadius}
              className='pointer-events-none'
            >
              {currentStep._Sections.map((section, sectionIndex) => {
                return (
                  <section key={`section-${step}-${sectionIndex}`} className='w-full'>
                    {section._Columns.map((column, columnIndex) => {
                      return (
                        <div key={`column-${step}-${sectionIndex}-${columnIndex}`} className='pointer-events-auto'>
                          {column._Controls.map((control) => {
                            return (
                              <PluginFormControl
                                key={`control-${step}-${control._Key}`}
                                language={language}
                                control={control}
                                plugin={authPlugin}
                                value={formState[control._Key] ?? ''}
                                onChange={(value) => {
                                  handleChange(control._Key, value)
                                }}
                                labelStyle={getTextColorStyles(
                                  labelColor,
                                  labelColorSolid,
                                  labelColorStartGradient,
                                  labelColorEndGradient,
                                  labelColorGradientDirection,
                                  currentParentBg ?? 'primary-90'
                                )}
                                error={formErrors[control._Key]}
                              />
                            )
                          })}
                        </div>
                      )
                    })}
                  </section>
                )
              })}

              {errorMessage && (
                <div className='mt-2 text-red-500' style={{ font: `var(--field-label-font)` }}>
                  <Trans>{errorMessage}</Trans>
                </div>
              )}
            </FormContainer>
          </div>

          <Button
            id={`${component._id}-submit-button`}
            colorVariant={buttonColorVariant}
            cornerRadius={buttonCornerRadius}
            onClick={handleAppLogin}
            disabled={loading || !isValidInput}
            padding={buttonPadding}
            size={buttonSize}
          >
            {loading ? (
              <>
                <span className='loading spin'></span>
              </>
            ) : (
              <>{stepActionLabel}</>
            )}
          </Button>
        </div>
      )}
    </BaseComponent>
  )

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        <Fragment>{loginFormComponent}</Fragment>
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return loginFormComponent
}
